<template>
	<div
		class="input-component"
		:class="{ 'not-icon': !wantIcon }"
	>
		<div
			class="label label-with-infoicon-containter"
			v-if="label"
		>
			<span> {{ label === '_' ? '\u00A0' : label }}</span>
			<InfoIcon
				v-if="infoIconObj"
				:infoIconObj="infoIconObj"
			/>
		</div>
		<div
			class="skeleton"
			:class="{
				match: valuesMatch,
				'no-match': valuesDontMatch,
				'icon-on-left': iconOnLeft,
			}"
		>
			<div
				class="saturn"
				:class="{ invalid: invalid }"
				@click="toggleShowError(null, $event)"
				@mouseover="toggleShowError(true, $event)"
				@mouseleave="toggleShowError(false, $event)"
			>
				<div class="saturn-textbox">
					<!--<input type="text" v-model="vm.saturn" :placeholder="placeholder" :readonly="readonly" :disabled="disabled" />-->
					<vue-numeric
						v-model="vm.saturn"
						:precision="'precision' in $props ? precision : 2"
						:currency="currency"
						:minus="allowNegative"
						:placeholder="placeholder"
						@input="inputHandler"
						:readonly="readonly"
						:disabled="disabled"
						:empty-value="null"
						@blur="$e => $emit('blur', $e)"
						:override="override"
						:autofocus="autofocus"
						@keyEnter="enterHandler"
					>
					</vue-numeric>

					<div
						class="saturn-textbox-error-message"
						v-if="errorHover && invalid"
					>
						<div v-if="hasValidationSlot">
							<slot name="validation"></slot>
						</div>
						<div v-else>Invalid Input</div>
					</div>
				</div>
				<div
					v-if="util.hasKey($slots, 'action')"
					class="action-slot"
				>
					<slot name="action"></slot>
				</div>
				<div
					v-else-if="!util.hasKey($slots, 'action') && wantIcon"
					class="saturn-icon"
				>
					<i :class="iconClass"></i>
				</div>
			</div>
			<div
				class="cdk"
				v-if="cdkValueExists"
				v-show="vstore.showCDK"
			>
				<div class="cdk-textbox">
					<input
						type="text"
						v-model="vm.cdk"
						class="cdk-textbox"
						readonly
					/>
				</div>
				<div class="cdk-icon">
					<div
						class="cdk-off"
						v-if="!valuesMatch"
					>
						<span
							class="cdk-paste"
							@click="copyValueFromCDK()"
							title="Update Saturn value with CDK value"
							><i class="fas fa-paste"></i
						></span>
						<span title="Values don't match"><i class="fas fa-exclamation-circle"></i></span>
					</div>
					<div>CDK</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import util from '@core/services/util';
	import vstore from '@core/services/vstore';
	import twoWayBind from '@core/services/two-way-bind';
	import VueNumeric from '@/components/customerApp/vue-numeric.vue';
	import InfoIcon from '@core/components/InfoIcon.vue';

	export default {
		name: 'InputCurrency',
		props: [
			'saturn',
			'cdk',
			'label',
			'placeholder',
			'invalid',
			'readonly',
			'disabled',
			'precision',
			'negative',
			'min',
			'max',
			'copy',
			'comparer',
			'iconSide',
			'showIcon',
			'icon',
			'symbol',
			'override',
			'infoIconObj',
			'autofocus',
		],
		emits: ['focus', 'blur', 'input', 'update:saturn', 'change', 'keyEnter', 'update:cdk'],
		data() {
			return {
				vstore: vstore.state,
				vm: {
					saturn: null,
					cdk: null,
				},
				errorHover: false,
				iconOnLeft: false,
				wantIcon: null,
				iconClass: 'fas fa-dollar-sign',
				currency: null,
			};
		},
		computed: {
			util() {
				return util;
			},
			valuesMatch() {
				if (this.comparer) return this.cdkValueExists && this.comparer(this.vm.saturn, this.vm.cdk);
				else return this.cdkValueExists && this.isNull(this.vm.saturn) === this.isNull(this.vm.cdk);
			},
			valuesDontMatch() {
				if (this.comparer) return this.cdkValueExists && !this.comparer(this.vm.saturn, this.vm.cdk);
				else return this.cdkValueExists && this.isNull(this.vm.saturn) !== this.isNull(this.vm.cdk);
			},
			cdkValueExists() {
				return this.$props['cdk'] !== undefined;
			},
			hasValidationSlot() {
				return !!this.$slots['validation'];
			},
			allowNegative() {
				return this.$props['negative'] !== undefined ? this.negative : true;
			},
		},
		mounted() {
			this.init();
			this.wantIcon = typeof this.showIcon != 'undefined' ? this.showIcon : true;
			this.currency = util.hasKey(this.$props, 'symbol') ? this.symbol : '';
			this.iconClass = util.hasKey(this.$props, 'icon') ? this.icon : 'fas fa-dollar-sign';

			twoWayBind.listen(this, this.vm);
			this.$watch(
				() => this.$props,
				function (newValue) {
					this.init();
				},
				{ deep: true, immediate: true },
			);

			this.$watch(
				() => this.vm.saturn,
				function (newValue) {
					this.$emit('change');
				},
				{ deep: true },
			);
		},
		created() {
			if (this.iconSide == 'left') {
				this.iconOnLeft = true;
			} else {
				this.iconOnLeft = false;
			}
		},
		methods: {
			copyValueFromCDK() {
				if (this.copy) this.vm.saturn = this.copy(this.vm.cdk);
				else this.vm.saturn = this.vm.cdk;
			},
			init() {
				this.vm.saturn = this.saturn != null && typeof this.saturn != 'undefined' ? parseFloat(this.saturn) : null;
				this.vm.cdk = this.cdk != null && typeof this.cdk != 'undefined' ? parseFloat(this.cdk) : null;
			},
			toggleShowError(override, event) {
				const isMobile = window.matchMedia('(max-width: 600px)').matches;

				if (event.type === 'click') {
					this.errorHover = !this.errorHover;
				} else if (!isMobile) {
					this.errorHover = override;
				}
			},
			isNull(val) {
				if (val === null) {
					return 0;
				} else {
					return parseFloat(val);
				}
			},
			inputHandler(value) {
				if (!isNaN(parseInt(value))) {
					this.$emit('update:saturn', value);
					this.$emit('input', value);
				}
			},
			enterHandler() {
				this.$emit('keyEnter');
			},
		},
		components: {
			VueNumeric,
			InfoIcon,
		},
	};
</script>
